import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import api from '../lib/api';
import { toast } from 'sonner';

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const NOMBRES_CLIC = {
  login_abrir: 'Botón Ingresar', registro_header: 'Prueba gratis (cabecera)', registro_hero: 'Prueba gratis (portada)',
  registro_final: 'Prueba gratis (final)', ver_receta: 'Ver ejemplo de receta',
};

function Kpi({ titulo, valor, nota }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground">{titulo}</p>
      <p className="text-2xl font-semibold mt-1">{valor ?? '—'}</p>
      {nota && <p className="text-xs text-muted-foreground mt-1">{nota}</p>}
    </Card>
  );
}

function Ranking({ titulo, filas, etiqueta = (n) => n }) {
  const max = Math.max(1, ...filas.map((f) => Number(f.n)));
  return (
    <Card className="p-4">
      <h3 className="font-medium mb-3">{titulo}</h3>
      {filas.length === 0 && <p className="text-sm text-muted-foreground">Sin datos todavía</p>}
      <div className="space-y-2">
        {filas.map((f) => (
          <div key={f.nombre} className="text-sm">
            <div className="flex justify-between gap-2"><span className="truncate" title={f.nombre}>{etiqueta(f.nombre)}</span><span className="tabular-nums">{f.n}</span></div>
            <div className="h-1.5 bg-muted rounded"><div className="h-1.5 bg-primary rounded" style={{ width: `${(100 * f.n) / max}%` }} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Heatmap({ celdas }) {
  const mapa = {};
  celdas.forEach((c) => { mapa[`${c.dia}-${c.hora}`] = Number(c.n); });
  const max = Math.max(1, ...Object.values(mapa));
  const pico = celdas.reduce((a, c) => (Number(c.n) > Number(a?.n || 0) ? c : a), null);
  return (
    <Card className="p-4 overflow-x-auto">
      <h3 className="font-medium">Días y horas con más visitas <span className="text-xs text-muted-foreground">(hora de Lima)</span></h3>
      {pico && <p className="text-xs text-muted-foreground mb-3">Pico: {DIAS[pico.dia - 1]} a las {pico.hora}:00</p>}
      <div className="grid gap-[2px] min-w-[640px]" style={{ gridTemplateColumns: '40px repeat(24, 1fr)' }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => <div key={h} className="text-[10px] text-center text-muted-foreground">{h}</div>)}
        {DIAS.map((d, i) => (
          <React.Fragment key={d}>
            <div className="text-xs text-muted-foreground">{d}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const n = mapa[`${i + 1}-${h}`] || 0;
              return <div key={h} title={`${d} ${h}:00 — ${n} visitas`} className="h-5 rounded-sm bg-primary" style={{ opacity: n ? 0.15 + 0.85 * (n / max) : 0.05 }} />;
            })}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
}

export default function WebStatsPage() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/web-stats/resumen?dias=${dias}`).then((r) => setData(r.data)).catch(() => toast.error('No se pudieron cargar las estadísticas'));
  }, [dias]);

  if (!data) return <p className="p-6 text-muted-foreground">Cargando estadísticas…</p>;
  const t = data.totales;
  const conversion = t.visitas ? `${((100 * t.registros) / t.visitas).toFixed(1)}%` : '—';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Visitas de la web</h1>
          <p className="text-sm text-muted-foreground">Landing pública app.clinia.lat/login · sin bots ni usuarios con sesión</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90, 365].map((d) => <Button key={d} size="sm" variant={d === dias ? 'default' : 'outline'} onClick={() => setDias(d)}>{d === 365 ? '1 año' : `${d} días`}</Button>)}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi titulo="Visitas hoy" valor={t.hoy} />
        <Kpi titulo="Visitas" valor={t.visitas} />
        <Kpi titulo="Personas únicas" valor={t.unicos} />
        <Kpi titulo="Tiempo medio" valor={t.duracion_media_s != null ? `${t.duracion_media_s} s` : null} />
        <Kpi titulo="Rebote" valor={t.rebote != null ? `${t.rebote}%` : null} nota="Se fueron sin interactuar" />
        <Kpi titulo="Solicitudes de registro" valor={t.registros} nota={`Conversión ${conversion}`} />
        <Kpi titulo="Inicios de sesión" valor={t.logins} />
      </div>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Visitas por día</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.por_dia}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip /><Legend />
            <Line type="monotone" dataKey="visitas" name="Visitas" stroke="#2563eb" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="unicos" name="Únicos" stroke="#10b981" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Heatmap celdas={data.heatmap} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Ranking titulo="Embudo de conversión" filas={data.embudo.map((e) => ({ nombre: e.paso, n: e.n }))} />
        <Ranking titulo="De dónde llegan" filas={data.origenes} />
        <Ranking titulo="Botones más usados" filas={data.clics} etiqueta={(n) => NOMBRES_CLIC[n] || n.replace('nav_', 'Menú: ').replace('faq_', 'Pregunta frecuente ')} />
        <Ranking titulo="Hasta dónde bajan (sesiones)" filas={data.scroll.map((s) => ({ ...s, nombre: `${s.nombre}%` }))} />
        <Ranking titulo="Dispositivo" filas={data.dispositivos} />
        <Ranking titulo="Sistema operativo" filas={data.sistemas} />
        <Ranking titulo="Navegador" filas={data.navegadores} />
        <Ranking titulo="Idioma del navegador" filas={data.idiomas} />
        <Ranking titulo="País" filas={data.paises} />
        <Ranking titulo="Campañas (UTM)" filas={data.campanas} />
        <Ranking titulo="Sitios que enlazan" filas={data.referrers} />
        <Ranking titulo="Tamaño de pantalla" filas={data.pantallas} />
      </div>

      <Card className="p-4 overflow-x-auto">
        <h3 className="font-medium mb-3">Últimas 25 visitas</h3>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground">{['Fecha y hora', 'Origen', 'Dispositivo', 'Navegador', 'Sistema', 'País', 'Campaña'].map((h) => <th key={h} className="pb-2 pr-4 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {data.ultimas.map((v, i) => (
              <tr key={i} className="border-t">
                <td className="py-1.5 pr-4 tabular-nums">{v.fecha}</td><td className="pr-4">{v.origen}</td><td className="pr-4">{v.dispositivo}</td>
                <td className="pr-4">{v.navegador}</td><td className="pr-4">{v.sistema}</td><td className="pr-4">{v.pais || '—'}</td><td>{v.utm_campaign || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
